import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, Copy, Ban, Trash2, RotateCcw, Users } from "lucide-react";
import {
  adminLogin,
  verifyAdminSession,
  adminListSubscribers,
  adminUpdateSubscriber,
  adminDeleteSubscriber,
  downloadSubscribersCsv,
} from "../lib/api";

const STORAGE_KEY = "salty-moo-admin";

// Canonical Admin classes — mirrors constants in pages/Admin.jsx per
// DEVELOPMENT_RULES.md §2d.
const inputCls =
  "w-full px-4 py-3 rounded-md bg-[#F9F6F0] border border-[#E7DFD5] focus:outline-none focus:border-[#4B3A2F] font-body text-[#2C2724]";
const labelCls =
  "text-xs uppercase tracking-[0.25em] text-[#4B3A2F] mb-2 block";

/** Compute the four dashboard stat tiles from the loaded roster. */
function computeStats(items) {
  const now = new Date();
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfWeek = new Date(startOfDay);
  startOfWeek.setDate(startOfDay.getDate() - startOfDay.getDay());
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  let total = 0;
  let today = 0;
  let thisWeek = 0;
  let thisMonth = 0;

  for (const s of items) {
    total += 1;
    const created = s.created_at ? new Date(s.created_at) : null;
    if (!created || Number.isNaN(created.getTime())) continue;
    if (created >= startOfDay) today += 1;
    if (created >= startOfWeek) thisWeek += 1;
    if (created >= startOfMonth) thisMonth += 1;
  }
  return { total, today, thisWeek, thisMonth };
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function StatTile({ testId, label, value, icon }) {
  return (
    <div
      data-testid={testId}
      className="parchment-deep page-shadow border border-[#E7DFD5] rounded-md p-5 flex items-center gap-4"
    >
      <div
        aria-hidden
        className="text-2xl"
        style={{ fontFamily: "'Bakerie', cursive" }}
      >
        {icon}
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#6D5A50]">
          {label}
        </p>
        <p className="font-display text-3xl text-[#4B3A2F] leading-none mt-1 tabular-nums">
          {value}
        </p>
      </div>
    </div>
  );
}

/** Small status pill matching the "Available / Coming Soon" pattern in Story Barn. */
function StatusPill({ status }) {
  const active = status !== "unsubscribed";
  return (
    <span
      className={`text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full ${
        active ? "bg-[#7E9268] text-white" : "bg-[#C8A876] text-[#2C2724]"
      }`}
    >
      {active ? "Active" : "Unsubscribed"}
    </span>
  );
}

export default function AdminSubscribers() {
  // Auth mirrors pages/Admin.jsx — same passcode, same localStorage key.
  const [passcode, setPasscode] = useState(
    () => localStorage.getItem(STORAGE_KEY) || "",
  );
  const [authed, setAuthed] = useState(false);
  useEffect(() => {
    if (!passcode) return;
    verifyAdminSession(passcode)
      .then(() => setAuthed(true))
      .catch(() => {
        localStorage.removeItem(STORAGE_KEY);
        setPasscode("");
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [passInput, setPassInput] = useState("");
  const [authBusy, setAuthBusy] = useState(false);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState(""); // "", "active", "unsubscribed"
  const [sort, setSort] = useState("newest");
  const [busyId, setBusyId] = useState("");

  const load = async (pc = passcode, opts = {}) => {
    setLoading(true);
    try {
      const data = await adminListSubscribers(pc, {
        q: opts.q ?? q,
        status: opts.status ?? statusFilter,
        sort: opts.sort ?? sort,
      });
      setItems(data.items || []);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast("Couldn\u2019t load subscribers", { description: detail });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authed) load();
  }, [authed]);

  // Debounced search — matches the "type to filter" feel of the rest of Admin.
  useEffect(() => {
    if (!authed) return;
    const t = setTimeout(() => load(passcode, { q, status: statusFilter, sort }), 250);
    return () => clearTimeout(t);
  }, [q, statusFilter, sort]);

  const stats = useMemo(() => computeStats(items), [items]);

  const submitLogin = async (e) => {
    e.preventDefault();
    setAuthBusy(true);
    try {
      const res = await adminLogin(passInput.trim());
      if (res.ok) {
        localStorage.setItem(STORAGE_KEY, res.token);
        setPasscode(res.token);
        setAuthed(true);
      }
    } catch (err) {
      toast(err?.response?.status === 429 ? err.response.data.detail : "Incorrect passcode", {
        description: "Ask Stefanie for the current admin passcode.",
      });
    } finally {
      setAuthBusy(false);
    }
  };

  const copyEmail = async (email) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(email);
      } else {
        const ta = document.createElement("textarea");
        ta.value = email;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        ta.remove();
      }
      toast("Email copied", { description: email });
    } catch {
      toast("Couldn\u2019t copy that email");
    }
  };

  const unsubscribe = async (row) => {
    const already = row.status === "unsubscribed";
    if (already) return;
    if (!window.confirm(`Mark ${row.email} as unsubscribed?`)) return;
    setBusyId(row.id);
    try {
      const res = await adminUpdateSubscriber(
        row.id,
        { status: "unsubscribed" },
        passcode,
      );
      setItems((cur) => cur.map((r) => (r.id === row.id ? res.item : r)));
      toast("Marked as unsubscribed", { description: row.email });
    } catch (err) {
      toast("Couldn\u2019t update", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setBusyId("");
    }
  };

  const reactivate = async (row) => {
    setBusyId(row.id);
    try {
      const res = await adminUpdateSubscriber(
        row.id,
        { status: "active" },
        passcode,
      );
      setItems((cur) => cur.map((r) => (r.id === row.id ? res.item : r)));
      toast("Marked as active", { description: row.email });
    } catch (err) {
      toast("Couldn\u2019t update", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setBusyId("");
    }
  };

  const removeRow = async (row) => {
    if (
      !window.confirm(
        `Permanently delete ${row.email}? This can\u2019t be undone.`,
      )
    )
      return;
    setBusyId(row.id);
    try {
      await adminDeleteSubscriber(row.id, passcode);
      setItems((cur) => cur.filter((r) => r.id !== row.id));
      toast("Deleted", { description: row.email });
    } catch (err) {
      toast("Couldn\u2019t delete", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setBusyId("");
    }
  };

  const downloadCsv = async () => {
    try {
      await downloadSubscribersCsv(passcode);
      toast("CSV downloaded");
    } catch (err) {
      toast("Couldn\u2019t export", { description: err.message });
    }
  };

  // ---------- Auth gate — mirrors pages/Admin.jsx ----------
  if (!authed) {
    return (
      <div className="parchment min-h-screen flex items-center justify-center px-6">
        <form
          onSubmit={submitLogin}
          data-testid="admin-subscribers-login"
          className="parchment-deep page-shadow max-w-md w-full p-10 rounded-md border border-[#E7DFD5] text-center"
        >
          <h1 className="font-display text-3xl text-[#4B3A2F] mb-2">
            Subscribers
          </h1>
          <p className="font-script text-xl text-[#4B3A2F] mb-6">
            the porch guest book
          </p>
          <label className={labelCls}>Admin passcode</label>
          <input
            data-testid="admin-subscribers-passcode-input"
            type="password"
            className={inputCls}
            value={passInput}
            onChange={(e) => setPassInput(e.target.value)}
            autoFocus
          />
          <button
            type="submit"
            disabled={authBusy || !passInput}
            data-testid="admin-subscribers-login-btn"
            className="btn-pasture mt-5 w-full justify-center"
          >
            {authBusy ? "Opening..." : "Open the porch"}
          </button>
        </form>
      </div>
    );
  }

  // ---------- Loaded dashboard ----------
  return (
    <div
      className="parchment min-h-screen pt-20 pb-20 px-6"
      data-testid="admin-subscribers-page"
    >
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4 mb-2">
          <div className="flex items-baseline gap-3">
            <Users size={22} className="text-[#4B3A2F]" />
            <h1 className="font-display text-4xl text-[#4B3A2F]">
              Subscribers
            </h1>
          </div>
          <Link
            to="/admin"
            data-testid="admin-subscribers-back-link"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#4B3A2F] hover:text-[#D62839] transition-colors"
          >
            <ArrowLeft size={14} />
            Back to Admin
          </Link>
        </div>
        <p className="font-script text-2xl text-[#4B3A2F] mb-8">
          everyone who&rsquo;s pulled up a rocking chair
        </p>

        {/* Stat tiles */}
        <div
          data-testid="admin-subscribers-stats"
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10"
        >
          <StatTile
            testId="stat-total"
            label="Total Subscribers"
            value={stats.total}
            icon={"\uD83D\uDC2E"}
          />
          <StatTile
            testId="stat-month"
            label="New this month"
            value={stats.thisMonth}
            icon={"\uD83C\uDF31"}
          />
          <StatTile
            testId="stat-week"
            label="New this week"
            value={stats.thisWeek}
            icon={"\uD83D\uDCE7"}
          />
          <StatTile
            testId="stat-today"
            label={"Today\u2019s signups"}
            value={stats.today}
            icon={"\uD83D\uDCC5"}
          />
        </div>

        {/* Toolbar: search + filters + sort + export */}
        <section
          data-testid="admin-subscribers-toolbar"
          className="parchment-deep page-shadow border border-[#E7DFD5] rounded-md p-6 mb-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div className="md:col-span-2">
              <label className={labelCls}>Search by name or email</label>
              <input
                data-testid="subscribers-search"
                type="search"
                placeholder="e.g. stefanie or @gmail"
                className={inputCls}
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <div>
              <label className={labelCls}>Filter</label>
              <select
                data-testid="subscribers-filter"
                className={inputCls}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All Subscribers</option>
                <option value="active">Active</option>
                <option value="unsubscribed">Unsubscribed</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Sort</label>
              <select
                data-testid="subscribers-sort"
                className={inputCls}
                value={sort}
                onChange={(e) => setSort(e.target.value)}
              >
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="alphabetical">Alphabetical (email)</option>
              </select>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-3 justify-between">
            <p className="text-xs uppercase tracking-[0.25em] text-[#6D5A50]">
              {loading ? "loading\u2026" : `${items.length} shown`}
            </p>
            <button
              type="button"
              data-testid="subscribers-export-btn"
              onClick={downloadCsv}
              className="btn-pasture inline-flex items-center gap-2"
            >
              <Download size={14} />
              Export CSV
            </button>
          </div>
        </section>

        {/* List */}
        <section
          data-testid="admin-subscribers-list-section"
          className="parchment-deep page-shadow border border-[#E7DFD5] rounded-md p-6"
        >
          {loading ? (
            <p className="text-sm text-[#6D5A50] italic px-2">{"loading\u2026"}</p>
          ) : items.length === 0 ? (
            <div
              data-testid="admin-subscribers-empty"
              className="text-center py-10 px-4"
            >
              <p className="font-script text-2xl text-[#4B3A2F]">
                the porch is quiet, friend
              </p>
              <p className="text-sm text-[#6D5A50] italic mt-2 max-w-md mx-auto">
                No subscribers match your filters yet. When someone joins The
                Salty Moo, they&rsquo;ll rock up right here.
              </p>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block">
                <table
                  data-testid="admin-subscribers-table"
                  className="w-full text-sm"
                >
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-[0.25em] text-[#6D5A50] border-b border-[#E7DFD5]">
                      <th className="py-3 pr-4">Name</th>
                      <th className="py-3 pr-4">Email</th>
                      <th className="py-3 pr-4">Signup date</th>
                      <th className="py-3 pr-4">Source</th>
                      <th className="py-3 pr-4">Status</th>
                      <th className="py-3 pr-0 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((row) => (
                      <tr
                        key={row.id || row.email}
                        data-testid={`subscribers-row-${row.id}`}
                        className="border-b border-[#E7DFD5]/70 last:border-0"
                      >
                        <td className="py-3 pr-4 text-[#2C2724]">
                          {row.name || (
                            <span className="text-[#6D5A50]/70 italic">
                              —
                            </span>
                          )}
                        </td>
                        <td className="py-3 pr-4 font-body text-[#2C2724]">
                          <span className="break-all">{row.email}</span>
                        </td>
                        <td className="py-3 pr-4 text-[#4B3A2F] tabular-nums">
                          {formatDate(row.created_at)}
                        </td>
                        <td className="py-3 pr-4 text-xs uppercase tracking-[0.2em] text-[#D62839]">
                          {row.source || (
                            <span className="text-[#6D5A50]/60 normal-case tracking-normal italic">
                              unknown
                            </span>
                          )}
                        </td>
                        <td className="py-3 pr-4">
                          <StatusPill status={row.status} />
                        </td>
                        <td className="py-3 pr-0 text-right whitespace-nowrap">
                          <div className="inline-flex items-center gap-2">
                            <button
                              type="button"
                              data-testid={`subscribers-copy-${row.id}`}
                              onClick={() => copyEmail(row.email)}
                              className="p-2 rounded-md border border-[#A87149] text-[#4B3A2F] hover:bg-[#A87149] hover:text-white transition-colors"
                              aria-label={`Copy ${row.email}`}
                              title="Copy email"
                            >
                              <Copy size={14} />
                            </button>
                            {row.status === "unsubscribed" ? (
                              <button
                                type="button"
                                data-testid={`subscribers-reactivate-${row.id}`}
                                disabled={busyId === row.id}
                                onClick={() => reactivate(row)}
                                className="p-2 rounded-md border border-[#7E9268] text-[#7E9268] hover:bg-[#7E9268] hover:text-white transition-colors disabled:opacity-50"
                                aria-label={`Reactivate ${row.email}`}
                                title="Mark active"
                              >
                                <RotateCcw size={14} />
                              </button>
                            ) : (
                              <button
                                type="button"
                                data-testid={`subscribers-unsubscribe-${row.id}`}
                                disabled={busyId === row.id}
                                onClick={() => unsubscribe(row)}
                                className="p-2 rounded-md border border-[#C8A876] text-[#8D6E63] hover:bg-[#C8A876] hover:text-[#2C2724] transition-colors disabled:opacity-50"
                                aria-label={`Unsubscribe ${row.email}`}
                                title="Unsubscribe"
                              >
                                <Ban size={14} />
                              </button>
                            )}
                            <button
                              type="button"
                              data-testid={`subscribers-delete-${row.id}`}
                              disabled={busyId === row.id}
                              onClick={() => removeRow(row)}
                              className="p-2 rounded-md border border-[#D62839] text-[#D62839] hover:bg-[#D62839] hover:text-white transition-colors disabled:opacity-50"
                              aria-label={`Delete ${row.email}`}
                              title="Delete"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile stacked cards */}
              <ul
                data-testid="admin-subscribers-cards"
                className="md:hidden space-y-3"
              >
                {items.map((row) => (
                  <li
                    key={row.id || row.email}
                    data-testid={`subscribers-card-${row.id}`}
                    className="bg-[#F9F6F0] border border-[#E7DFD5] rounded-md p-4"
                  >
                    <div className="flex items-baseline justify-between gap-3 mb-1">
                      <p className="font-display text-lg text-[#4B3A2F] truncate">
                        {row.name || row.email}
                      </p>
                      <StatusPill status={row.status} />
                    </div>
                    {row.name && (
                      <p className="text-sm text-[#2C2724] break-all">
                        {row.email}
                      </p>
                    )}
                    <div className="mt-2 flex items-baseline gap-3 flex-wrap text-[10px] uppercase tracking-[0.25em] text-[#6D5A50]">
                      <span>{formatDate(row.created_at)}</span>
                      {row.source && (
                        <span className="text-[#D62839]">{row.source}</span>
                      )}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        data-testid={`subscribers-copy-m-${row.id}`}
                        onClick={() => copyEmail(row.email)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#A87149] text-[#4B3A2F] text-xs hover:bg-[#A87149] hover:text-white transition-colors"
                      >
                        <Copy size={12} /> Copy
                      </button>
                      {row.status === "unsubscribed" ? (
                        <button
                          type="button"
                          data-testid={`subscribers-reactivate-m-${row.id}`}
                          disabled={busyId === row.id}
                          onClick={() => reactivate(row)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#7E9268] text-[#7E9268] text-xs hover:bg-[#7E9268] hover:text-white transition-colors disabled:opacity-50"
                        >
                          <RotateCcw size={12} /> Reactivate
                        </button>
                      ) : (
                        <button
                          type="button"
                          data-testid={`subscribers-unsubscribe-m-${row.id}`}
                          disabled={busyId === row.id}
                          onClick={() => unsubscribe(row)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#C8A876] text-[#8D6E63] text-xs hover:bg-[#C8A876] hover:text-[#2C2724] transition-colors disabled:opacity-50"
                        >
                          <Ban size={12} /> Unsubscribe
                        </button>
                      )}
                      <button
                        type="button"
                        data-testid={`subscribers-delete-m-${row.id}`}
                        disabled={busyId === row.id}
                        onClick={() => removeRow(row)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#D62839] text-[#D62839] text-xs hover:bg-[#D62839] hover:text-white transition-colors disabled:opacity-50"
                      >
                        <Trash2 size={12} /> Delete
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>

        <p className="text-xs italic text-[#6D5A50] mt-8 text-center">
          Subscribers live in your MongoDB. When you connect Substack (or add a
          native form), they&rsquo;ll appear here automatically.
        </p>
      </div>
    </div>
  );
}
